import { NextSeoProps } from "next-seo";
import { z, ZodFormattedError } from "zod";
import { LandingPageEvent } from "~/common/constants";
import { heDecodeOrDefault } from "~/lib/shared/html-utils";
import { LandingPageStructDataProps } from "~/types/asgard/StructuredData";

const decode = (str: string | unknown): string => heDecodeOrDefault(str, "");

const appearanceSchema = z.union([
  z.literal("brand"),
  z.literal("dark"),
  z.literal("light"),
]);

const linkSchema = z.object({
  url: z.string().url(),
  title: z.string().transform(decode),
});

const imageSchema = z.object({
  id: z.union([z.number(), z.string()]),
  title: z.string().nullable().transform(decode),
  url: z.string().url(),
  alt: z.string().nullable().transform(decode),
  mimeType: z.string(),
  width: z.number(),
  height: z.number(),
  sizes: z
    .object({
      thumbnail: z.string().url(),
      "thumbnail-width": z.number(),
      "thumbnail-height": z.number(),
      medium: z.string(),
      "medium-width": z.number(),
      "medium-height": z.number(),
      mediumLarge: z.string(),
      "mediumLarge-width": z.number(),
      "mediumLarge-height": z.number(),
      large: z.string(),
      "large-width": z.number(),
      "large-height": z.number(),
      thumbnailRetina: z.string(),
      "thumbnailRetina-width": z.number(),
      "thumbnailRetina-height": z.number(),
      mobileRetina: z.string(),
      "mobileRetina-width": z.number(),
      "mobileRetina-height": z.number(),
      desktopRetina: z.string(),
      "desktopRetina-width": z.number(),
      "desktopRetina-height": z.number(),
    })
    .optional(),
});

const newsletterImageSchema = z.object({
  source: z.string(),
  caption: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  altText: z.string().nullable(),
});

const statSchema = z.object({
  name: z.string().transform(decode),
  value: z.string().transform(decode),
});

export enum LandingPageBlockTypes {
  ContentWithImage = "content_with_image",
  ContentWithImageQuoteCTA = "content_image_quote_cta",
  CTASimpleCentered = "cta_simple_centered",
  CTASplitWithImage = "cta_split_with_image",
  CTATwoColumnWithImage = "cta_two_column_with_image",
  FAQ = "faq",
  FeatureWithDemo = "feature_with_demo",
  FeatureTwoByTwoGrid = "feature_2x2_grid",
  FeatureTwoTextColumns = "feature_two_text_columns",
  FeatureWithCTAs = "feature_with_ctas",
  HeaderSimple = "header_simple",
  HeaderWithBackgroundImage = "header_with_background_image",
  HeaderWithCards = "header_with_cards",
  HeroCentered = "hero_centered",
  HeroNewsletterSignup = "hero_with_ad",
  HeroWithDemo = "hero_with_demo",
  HeroWithScreenshot = "hero_with_screenshot",
  HeroWithRightImage = "hero_with_right_image",
  NewsletterSignup = "newsletter_signup",
  StatsGrid = "stats_grid",
  StatsSimple = "stats_simple",
  StatsWithImage = "stats_with_image",
  TestimonialSimpleCentered = "testimonial_simple_centered",
  Wysiwyg = "wysiwyg",
  YoutubeEmbed = "youtube_video",
  TaggedPosts = "tagged_posts",
  InvalidProperties = "invalid_properties",
  UnknownBlockType = "invalid_blocktype",
}

const landingPageBlockTypesSchema = z.nativeEnum(LandingPageBlockTypes);

const baseLandingPageBlockSchema = z.object({
  acfFcLayout: landingPageBlockTypesSchema,
});

export type BaseLandingPageBlock = z.infer<typeof baseLandingPageBlockSchema>;

const contentBlockSchema = z.union([
  z.object({
    acfFcLayout: z.literal("paragraph"),
    display: z.union([z.literal("normal"), z.literal("large")]),
    paragraph: z.string(),
  }),
  z.object({
    acfFcLayout: z.literal("header"),
    header: z.string(),
  }),
  z.object({
    acfFcLayout: z.literal("bullets"),
    bullets: z.array(
      z.object({
        title: z.string(),
        description: z.string(),
      })
    ),
  }),
  z.object({
    acfFcLayout: z.literal("stats"),
    stats: z.array(statSchema),
  }),
]);

export const contentWithImageSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.ContentWithImage),
  appearance: appearanceSchema,
  imagePosition: z.union([z.literal("left"), z.literal("right-sticky")]),
  image: imageSchema,
  eyebrow: z.string(),
  headline: z.string(),
  subContentBlocks: z.array(contentBlockSchema),
});

export type ContentWithImage = z.infer<typeof contentWithImageSchema>;

export const contentWithImageQuoteCTASchema = contentWithImageSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.ContentWithImageQuoteCTA),
  primaryCta: linkSchema,
  quote: z
    .object({
      body: z.string(),
      authorName: z.string(),
      authorDetails: z.string(),
    })
    .nullable(),
});

export type ContentWithImageQuoteCTA = z.infer<
  typeof contentWithImageQuoteCTASchema
>;

export const CTASimpleCenteredSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.CTASimpleCentered),
  appearance: appearanceSchema,
  headline: z.string().transform(decode),
  description: z.string().transform(decode),
  primaryCtaLink: linkSchema,
  secondaryCtaLink: linkSchema.nullable(),
});

export type CTASimpleCentered = z.infer<typeof CTASimpleCenteredSchema>;

export const CTASplitWithImageSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.CTASplitWithImage),
  appearance: appearanceSchema,
  image: imageSchema,
  eyebrow: z.string().transform(decode),
  headline: z.string().transform(decode),
  description: z.string().transform(decode),
  primaryCtaLink: linkSchema,
});

export type CTASplitWithImage = z.infer<typeof CTASplitWithImageSchema>;

const bulletSchema = z.object({
  bulletPoint: z.string().transform(decode),
});

export const CTATwoColumnWithImageSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.CTATwoColumnWithImage),
  appearance: appearanceSchema,
  image: imageSchema,
  headline: z.string().transform(decode),
  description: z.string().transform(decode),
  bullets: z.array(bulletSchema),
  primaryCtaLink: linkSchema,
});

export type CTATwoColumnWithImage = z.infer<typeof CTATwoColumnWithImageSchema>;

export const headerSimpleSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.HeaderSimple),
  appearance: appearanceSchema,
  description: z.string().transform(decode),
  eyebrow: z.string().transform(decode),
  title: z.string().transform(decode),
  titleLevel: z
    .union([z.literal("h2"), z.literal("h3"), z.literal("h4")])
    .nullable(),
  textLocation: z
    .union([z.literal("center"), z.literal("end"), z.literal("start")])
    .nullable(),
  textAlignment: z
    .union([z.literal("center"), z.literal("end"), z.literal("start")])
    .nullable(),
});

export type HeaderSimple = z.infer<typeof headerSimpleSchema>;

export const headerWithBackgroundImageSchema =
  baseLandingPageBlockSchema.extend({
    acfFcLayout: z.literal(LandingPageBlockTypes.HeaderWithBackgroundImage),
    appearance: z.union([z.literal("dark_image"), z.literal("light_image")]),
    image: imageSchema,
    description: z.string().transform(decode),
    eyebrow: z.string().transform(decode),
    title: z.string().transform(decode),
    titleLevel: z
      .union([z.literal("h2"), z.literal("h3"), z.literal("h4")])
      .nullable(),
    textLocation: z
      .union([z.literal("center"), z.literal("end"), z.literal("start")])
      .nullable(),
    textAlignment: z
      .union([z.literal("center"), z.literal("end"), z.literal("start")])
      .nullable(),
  });

export type HeaderWithBackgroundImage = z.infer<
  typeof headerWithBackgroundImageSchema
>;

export const headerWithCardsSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.HeaderWithCards),
  appearance: appearanceSchema,
  description: z.string().transform(decode),
  eyebrow: z.string().transform(decode),
  title: z.string().transform(decode),
  titleLevel: z
    .union([z.literal("h2"), z.literal("h3"), z.literal("h4")])
    .nullable(),
  cards: z.array(
    z.object({
      name: z.string().transform(decode),
      description: z.string().transform(decode),
    })
  ),
  image: imageSchema.nullable(),
  textLocation: z
    .union([z.literal("center"), z.literal("end"), z.literal("start")])
    .nullable(),
  textAlignment: z
    .union([z.literal("center"), z.literal("end"), z.literal("start")])
    .nullable(),
});

export type HeaderWithCards = z.infer<typeof headerWithCardsSchema>;

export const heroCenteredSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.HeroCentered),
  appearance: z.union([z.literal("dark"), z.literal("light")]),
  backgroundImage: imageSchema.nullable(),
  kickerCtaDescription: z.string().nullable().transform(decode),
  kickerCtaLink: linkSchema.nullable(),
  headline: z.string().transform(decode),
  description: z.string().transform(decode),
  primaryCtaLink: linkSchema,
  secondaryCtaLink: linkSchema.nullable(),
});

export type HeroCentered = z.infer<typeof heroCenteredSchema>;

export const heroWithDemoSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.HeroWithDemo),
  appearance: z.union([z.literal("dark"), z.literal("light")]),
  description: z.string().nullable().transform(decode),
  eventName: z.nativeEnum(LandingPageEvent),
  image: imageSchema,
  openQuestion: z.string().nullable().transform(decode),
  openQuestionCharacterLimit: z.number().nullable(),
  successMessage: z.string(),
  salesforceCampaignId: z.string(),
  successRedirectUrl: linkSchema,
  title: z.string(),
});

export type HeroWithDemo = z.infer<typeof heroWithDemoSchema>;

export const heroWithRightImageSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.HeroWithRightImage),
  appearance: z.union([z.literal("straight"), z.literal("angled")]),
  description: z.string().transform(decode),
  headline: z.string().transform(decode),
  iconImage: imageSchema.nullable(),
  image: imageSchema,
  kickerCtaDescription: z.string().nullable().transform(decode),
  kickerCtaLink: linkSchema.nullable(),
  primaryCtaLink: linkSchema,
  secondaryCtaLink: linkSchema.nullable(),
});

export type HeroWithRightImage = z.infer<typeof heroWithRightImageSchema>;

export const heroWithScreenshotSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.HeroWithScreenshot),
  appearance: z.union([z.literal("light"), z.literal("dark")]),
  imagePlacement: z.union([z.literal("offset"), z.literal("beneath")]),
  image: imageSchema,
  headline: z.string().transform(decode),
  description: z.string().transform(decode),
  primaryCtaLink: linkSchema,
  secondaryCtaLink: linkSchema.nullable(),
});

export type HeroWithScreenshot = z.infer<typeof heroWithScreenshotSchema>;

const adSchema = z.object({
  adType: z.union([
    z.literal("inline_newsletter"),
    z.literal("sticky_newsletter"),
  ]),
  iterableListId: z.number(),
  newsletterDescription: z.string(),
  newsletterImage: newsletterImageSchema.nullable(),
  newsletterTitle: z.string(),
  slug: z.string(),
});

export const heroNewsletterSignupSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.HeroNewsletterSignup),
  appearance: z.union([z.literal("light"), z.literal("dark")]),
  backgroundImage: imageSchema.nullable(),
  headline: z.string().transform(decode),
  description: z.string().transform(decode),
  ad: adSchema,
});

export type HeroNewsletterSignup = z.infer<typeof heroNewsletterSignupSchema>;

export const newsletterSignupSchema = z.object({
  acfFcLayout: z.literal(LandingPageBlockTypes.NewsletterSignup),
  appearance: appearanceSchema,
  ad: adSchema,
  backgroundImage: imageSchema.nullable(),
  variant: z.union([z.literal("inline_center"), z.literal("side_by_side")]),
});

export type NewsletterSignup = z.infer<typeof newsletterSignupSchema>;

export const statsSimpleSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.StatsSimple),
  appearance: appearanceSchema,
  stats: z.array(statSchema),
});

export type StatsSimple = z.infer<typeof statsSimpleSchema>;

export const statsGridSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.StatsGrid),
  appearance: appearanceSchema,
  title: z.string().transform(decode),
  description: z.string().transform(decode),
  stats: z.array(statSchema),
});

export type StatsGrid = z.infer<typeof statsGridSchema>;

export const statsWithImageSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.StatsWithImage),
  appearance: appearanceSchema,
  image: imageSchema,
  imageOrientation: z.union([z.literal("start"), z.literal("end")]),
  eyebrow: z.string().transform(decode),
  title: z.string().transform(decode),
  description: z.string().transform(decode),
  stats: z.array(statSchema),
});

export type StatsWithImage = z.infer<typeof statsWithImageSchema>;

const faqSchema = z.object({
  question: z.string().transform(decode),
  answer: z.string().transform(decode),
});

export const FAQSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.FAQ),
  appearance: appearanceSchema,
  display: z.union([z.literal("accordion"), z.literal("side-by-side")]),
  faqs: z.array(faqSchema),
  title: z.string().nullable().transform(decode),
});

export type FAQ = z.infer<typeof FAQSchema>;

const featureSchema = z.object({
  name: z.string().transform(decode),
  description: z.string().transform(decode),
});

export const featureWithDemoSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.FeatureWithDemo),
  appearance: z.union([z.literal("dark"), z.literal("light")]),
  layout: z.union([z.literal("centered"), z.literal("split")]),
  description: z.string().nullable().transform(decode),
  salesforceCampaignId: z.string(),
  openQuestion: z.string().nullable().transform(decode),
  openQuestionCharacterLimit: z.coerce.number().nullable(),
  successMessage: z.string(),
  successRedirectUrl: linkSchema,
  title: z.string(),
});

export type FeatureWithDemo = z.infer<typeof featureWithDemoSchema>;

export const FeatureTwoByTwoGridSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.FeatureTwoByTwoGrid),
  appearance: z.union([z.literal("dark"), z.literal("light")]),
  features: z.array(featureSchema),
  description: z.string().nullable().transform(decode),
  eyebrow: z.string().nullable().transform(decode),
  title: z.string().transform(decode),
});

export type FeatureTwoByTwoGrid = z.infer<typeof FeatureTwoByTwoGridSchema>;

const featureWithCTASchema = featureSchema.extend({
  ctaLink: linkSchema,
});

export const FeatureWithCTAsSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.FeatureWithCTAs),
  appearance: z.union([z.literal("dark"), z.literal("light")]),
  features: z.array(featureWithCTASchema),
  description: z.string().nullable().transform(decode),
  eyebrow: z.string().nullable().transform(decode),
  title: z.string().transform(decode),
});

export type FeatureWithCTAs = z.infer<typeof FeatureWithCTAsSchema>;

const paragraphSchema = z.object({
  paragraph: z.string(),
});

export const FeatureTwoTextColumnsSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.FeatureTwoTextColumns),
  appearance: z.union([z.literal("dark"), z.literal("light")]),
  eyebrow: z.string().nullable().transform(decode),
  title: z.string().transform(decode),
  leftParagraphs: z.array(paragraphSchema).default([]),
  rightParagraphs: z.array(paragraphSchema).default([]),
  image: imageSchema.nullable(),
});

export type FeatureTwoTextColumns = z.infer<typeof FeatureTwoTextColumnsSchema>;

export const WysiwygSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.Wysiwyg),
  appearance: appearanceSchema,
  wysiwygTextField: z.string(),
});

export type WysiwygBlock = z.infer<typeof WysiwygSchema>;

export const testimonialSimpleCenteredPropsSchema =
  baseLandingPageBlockSchema.extend({
    acfFcLayout: z.literal(LandingPageBlockTypes.TestimonialSimpleCentered),
    appearance: appearanceSchema,
    quote: z.string(),
    personsName: z.string(),
    personsTitle: z.string().nullable(),
    personImage: imageSchema,
    logoImage: imageSchema,
  });

export type TestimonialSimpleCentered = z.infer<
  typeof testimonialSimpleCenteredPropsSchema
>;

export const youtubeEmbedSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.YoutubeEmbed),
  videoUrl: z.string(),
});

export type YoutubeEmbedACFBlock = z.infer<typeof youtubeEmbedSchema>;

const termSummarySchema = z.object({
  termId: z.number(),
  name: z.string(),
  slug: z.string(),
});

const postSummarySchema = z.object({
  id: z.number(),
  title: z.string(),
  slug: z.string(),
  type: z.string(),
  dateGmt: z.string(),
  subhead: z.string().optional().nullable(),
  image: newsletterImageSchema.optional(),
  primaryCategory: termSummarySchema.optional(),
  primaryTag: termSummarySchema.optional(),
});

export const taggedPostsSchema = baseLandingPageBlockSchema.extend({
  acfFcLayout: z.literal(LandingPageBlockTypes.TaggedPosts),
  header: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  contentTags: z.array(postSummarySchema),
  ctaUrl: linkSchema.optional().nullable(),
});

export type TaggedPostsACFBlock = z.infer<typeof taggedPostsSchema>;

export type InvalidProperties = {
  acfFcLayout: LandingPageBlockTypes.InvalidProperties;
  blockName: string;
  error: ZodFormattedError<unknown>;
};

export type UnknownBlockType = {
  acfFcLayout: LandingPageBlockTypes.UnknownBlockType;
  block: unknown;
};

export type LandingPageBlock =
  | ContentWithImage
  | ContentWithImageQuoteCTA
  | CTASimpleCentered
  | CTASplitWithImage
  | CTATwoColumnWithImage
  | FAQ
  | FeatureWithDemo
  | FeatureTwoByTwoGrid
  | FeatureTwoTextColumns
  | FeatureWithCTAs
  | HeaderSimple
  | HeaderWithBackgroundImage
  | HeaderWithCards
  | HeroCentered
  | HeroNewsletterSignup
  | HeroWithDemo
  | HeroWithRightImage
  | HeroWithScreenshot
  | InvalidProperties
  | NewsletterSignup
  | StatsSimple
  | StatsGrid
  | StatsWithImage
  | TestimonialSimpleCentered
  | TaggedPostsACFBlock
  | UnknownBlockType
  | WysiwygBlock
  | YoutubeEmbedACFBlock;

export interface LandingPage {
  id: number;
  title: string;
  slug: string;
  type: string;
  hideFooter: boolean;
  blocks: Array<LandingPageBlock>;
  seo: NextSeoProps;
  structuredData: LandingPageStructDataProps;
}
